/**
 * AskCoachPill — the 2.0 room's one phone door (P9.1, Part 8 frame 1ao): "Ask a coach" as a pill in the top bar.
 *
 * It opens the room with NOTHING called (`/chat`, no query, no router state), never a per-coach thread (P9.2).
 * From AX1 up it shows the glyph only and keeps the accessible name "Ask a coach" (the label is hidden by the
 * `data-type-scale='ax'` attribute `TypeRampRoot` writes, in the stylesheet).
 *
 * Where it shows (spec D10, wave 1 Q3): signed-in athlete and dual accounts, on Today, Train, Races, Team, and
 * Settings on the web. Never on Run (chrome-free), never for a guardian-only or a coach-only account, never in
 * the native app's Settings. The path test is an allowlist, so any other route that ever mounts a top bar
 * (approvals, guardian home) draws no pill.
 *
 * It is a gate and a body on purpose: with `chat_2_0_v1` off only the flag is read and nothing renders, so a
 * top bar that mounts it draws exactly what it drew before and none of the role hooks run.
 *
 * Its one caller is `TopBar.tsx`, between the logo and the right-hand controls (so every screen that mounts the
 * top bar carries it: Today, Races, Team, Settings, and the Train shell). With the flag off it renders nothing,
 * so the bar's markup is exactly what it was.
 *
 * Width: the pill is the 44px glyph circle by default and shows its label only from 480px up, the width where it
 * sits centred with 8px of clear space to the logo and to the bug-report button's hit area (the sums are in
 * `TopBar.askCoach.css.test.ts`). `data-ask-coach-pill` is the hook `TopBar.module.css` uses to centre it and to
 * reserve the bug button's hit ring.
 */
import { Link, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAccountRole } from '../../hooks/useAccountRole';
import { useGuardianMode } from '../../hooks/useGuardianMode';
import { isNativeApp } from '../../lib/platform';
import styles from './AskCoachPill.module.css';
import { useRoomEntryFlag } from './useRoomEntryFlag';

/** Today, Train (the whole `/plan` tree), Races and Team; the router matches paths without regard to case. */
const EVERYWHERE = /^\/(today|plan|races|team)(\/|$)/i;
/** Settings: the web only. */
const WEB_ONLY = /^\/settings(\/|$)/i;

/** True on the routes the pill may draw on (basename-stripped path). `/run` and every unlisted route are false. */
export function askCoachPillRoute(pathname: string, native: boolean): boolean {
  return EVERYWHERE.test(pathname) || (!native && WEB_ONLY.test(pathname));
}

export function AskCoachPill(): JSX.Element | null {
  // With the cached hint (`useRoomEntryFlag`): a returning flag-on device draws the pill from the first frame
  // (the role hold below still applies) instead of popping it in, and the bar does not re-lay out.
  const roomOn = useRoomEntryFlag();
  return roomOn ? <AskCoachPillBody /> : null;
}

function AskCoachPillBody(): JSX.Element | null {
  const { user } = useAuthContext();
  const { guardianOnly } = useGuardianMode();
  // The coach-boundary answer: hold (draw nothing) while it is pending, so a coach never sees the pill flash.
  const { coachOnly, boundaryPending } = useAccountRole();
  const { pathname } = useLocation();

  if (!user || guardianOnly || coachOnly || boundaryPending) return null;
  if (!askCoachPillRoute(pathname, isNativeApp())) return null;

  return (
    <Link to="/chat" className={styles.pill} aria-label="Ask a coach" data-ask-coach-pill="">
      <span className={styles.glyph} aria-hidden="true">
        <svg
          width="17"
          height="17"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 9a2 2 0 0 1-2 2H6.5L3.5 13.5V11H4a2 2 0 0 1-2-2V4.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
        </svg>
      </span>
      <span className={styles.label}>Ask a coach</span>
    </Link>
  );
}
